import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, AlertTriangle, Trash2, Trophy, XCircle, FileUp, Sparkles, Download, Send } from "lucide-react";
import api, { apiError } from "@/lib/api";
import ImportSheet from "@/components/ImportSheet";
import ContactsPanel from "@/components/ContactsPanel";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";

const STAGES = ["Identified", "Qualifying", "Proposal In Progress", "Submitted", "Won", "Lost"];
const SOURCES = ["sourced bid", "referral", "outreach"];
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);

const empty = { client_name: "", value: "", due_date: "", stage: "Identified", source: "outreach", next_action: "", next_action_date: "", notes: "", probability: "", vertical: "", client_type: "" };

function OppForm({ initial, onSaved, onClose }) {
  const [form, setForm] = useState(initial || empty);
  const [kickoff, setKickoff] = useState(initial?.kickoff || "");
  const [kloading, setKloading] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/opportunities/${form.id}/kickoff/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kickoff-${(form.client_name || "deal").replace(/[^a-z0-9]+/gi, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const emailKickoff = async () => {
    if (!emailTo.trim()) return toast.error("Enter a recipient email");
    setSending(true);
    try {
      const { data } = await api.post(`/opportunities/${form.id}/kickoff/email`, { to_email: emailTo.trim() }, { timeout: 60000 });
      toast.success(data.detail);
      setEmailTo("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const runKickoff = async () => {
    setKloading(true);
    try {
      const { data } = await api.post(`/opportunities/${form.id}/kickoff`, {}, { timeout: 180000 });
      setKickoff(data.kickoff);
      toast.success("Kickoff package drafted");
    } catch (e) { toast.error(apiError(e)); }
    finally { setKloading(false); }
  };
  const save = async () => {
    if (!form.client_name || !form.next_action || !form.next_action_date) {
      toast.error("Client, next action and next action date are required");
      return;
    }
    const payload = { ...form, value: form.value === "" || form.value == null ? null : Number(form.value),
      probability: form.probability === "" || form.probability == null ? null : Math.min(100, Math.max(0, Number(form.probability))) };
    try {
      if (form.id) await api.put(`/opportunities/${form.id}`, payload);
      else await api.post("/opportunities", payload);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    await api.delete(`/opportunities/${form.id}`);
    toast("Deleted");
    onSaved();
    onClose();
  };
  return (
    <div className="space-y-3 pb-6">
      <input data-testid="opp-client-input" className="tactical-input" placeholder="Client / agency name *" value={form.client_name} onChange={set("client_name")} />
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="opp-value-input" type="number" className="tactical-input" placeholder="Value estimate $" value={form.value ?? ""} onChange={set("value")} />
        <div>
          <p className="overline-label mb-1">Due date</p>
          <input data-testid="opp-due-input" type="date" className="tactical-input" value={form.due_date || ""} onChange={set("due_date")} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select data-testid="opp-stage-select" className="tactical-input" value={form.stage} onChange={set("stage")}>
          {STAGES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="opp-source-select" className="tactical-input" value={form.source} onChange={set("source")}>
          {SOURCES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <input data-testid="opp-next-action-input" className="tactical-input" placeholder="Next action *" value={form.next_action || ""} onChange={set("next_action")} />
      <div className="grid grid-cols-3 gap-2">
        <div>
          <p className="overline-label mb-1">Win prob %</p>
          <input data-testid="opp-probability-input" type="number" min="0" max="100" className="tactical-input" placeholder="—" value={form.probability ?? ""} onChange={set("probability")} />
        </div>
        <div>
          <p className="overline-label mb-1">Vertical</p>
          <select data-testid="opp-vertical-select" className="tactical-input px-1.5" value={form.vertical || ""} onChange={set("vertical")}>
            <option value="">—</option>
            {["EDU", "WFD", "BH", "Other"].map((v) => <option key={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <p className="overline-label mb-1">Client type</p>
          <select data-testid="opp-client-type-select" className="tactical-input px-1.5" value={form.client_type || ""} onChange={set("client_type")}>
            <option value="">—</option>
            {["Charter School", "School District", "Government Agency", "Healthcare", "Other"].map((v) => <option key={v}>{v}</option>)}
          </select>
        </div>
      </div>
      <div>
        <p className="overline-label mb-1">Next action date *</p>
        <input data-testid="opp-next-action-date-input" type="date" className="tactical-input" value={form.next_action_date || ""} onChange={set("next_action_date")} />
      </div>
      <textarea data-testid="opp-notes-input" className="tactical-input h-24 py-2" placeholder="Notes" value={form.notes || ""} onChange={set("notes")} />
      {form.id && (
        <div className="space-y-2">
          <button
            data-testid="opp-kickoff-button"
            onClick={runKickoff}
            disabled={kloading}
            className="w-full h-11 border border-primary text-primary text-xs font-bold uppercase tracking-widest tap-btn disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            <Sparkles size={13} />{kloading ? "AI drafting kickoff..." : kickoff ? "Regenerate AI Kickoff" : "AI Proposal Kickoff"}
          </button>
          {kickoff && (
            <div data-testid="kickoff-content" className="border border-border bg-secondary/40 p-3 text-xs whitespace-pre-wrap max-h-64 overflow-y-auto leading-relaxed">
              {kickoff}
            </div>
          )}
          {kickoff && (
            <div className="space-y-2">
              <button data-testid="kickoff-pdf-button" onClick={downloadPdf} className="w-full h-10 border border-border text-foreground text-[11px] font-bold uppercase tracking-widest tap-btn flex items-center justify-center gap-1.5">
                <Download size={13} />Download PDF
              </button>
              <div className="flex gap-2">
                <input data-testid="kickoff-email-input" type="email" className="tactical-input h-10" placeholder="team@yourcompany.com" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} />
                <button data-testid="kickoff-email-button" onClick={emailKickoff} disabled={sending} className="h-10 px-4 bg-primary text-primary-foreground text-[11px] font-bold uppercase tracking-widest tap-btn disabled:opacity-50 flex items-center gap-1.5 shrink-0">
                  <Send size={12} />{sending ? "Sending" : "Email"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <button data-testid="opp-save-button" onClick={save} className="w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm tap-btn">Save</button>
      {form.id && (
        <button data-testid="opp-delete-button" onClick={del} className="w-full h-11 border border-destructive text-destructive text-xs font-bold uppercase tracking-widest tap-btn flex items-center justify-center gap-2"><Trash2 size={14} />Delete</button>
      )}
    </div>
  );
}

export default function Pipeline() {
  const [opps, setOpps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [view, setView] = useState("deals");

  const load = useCallback(() => {
    api.get("/opportunities").then((r) => setOpps(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const markOutcome = async (o, stage) => {
    try {
      await api.put(`/opportunities/${o.id}`, { stage });
      toast.success(stage === "Won" ? `${o.client_name} marked AWARDED` : `${o.client_name} marked not awarded`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="pipeline-page">
      <div className="px-5 pt-8 pb-4 flex items-end justify-between">
        <div>
          <p className="overline-label text-primary">Pipeline</p>
          <h1 className="font-display text-4xl tracking-tight text-primary">Deals</h1>
        </div>
        <div className="flex gap-2">
          <Sheet open={importOpen} onOpenChange={setImportOpen}>
            <SheetTrigger asChild>
              <button data-testid="import-doc-button" className="h-11 px-3 border border-primary text-primary font-bold text-xs uppercase tracking-widest tap-btn flex items-center gap-1">
                <FileUp size={14} />Import
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-background border-border rounded-t-2xl max-h-[90vh] overflow-y-auto">
              <SheetHeader><SheetTitle className="font-display tracking-tight text-left text-primary">Import from Document</SheetTitle></SheetHeader>
              <ImportSheet onImported={load} onClose={() => setImportOpen(false)} />
            </SheetContent>
          </Sheet>
          <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <SheetTrigger asChild>
              <button data-testid="add-opportunity-button" onClick={() => setEditing(null)} className="h-11 px-4 bg-primary text-primary-foreground font-bold text-xs uppercase tracking-widest tap-btn flex items-center gap-1">
                <Plus size={14} />New
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-background border-border rounded-t-2xl max-h-[90vh] overflow-y-auto">
              <SheetHeader><SheetTitle className="font-display tracking-tight text-left text-primary">{editing ? "Edit Opportunity" : "New Opportunity"}</SheetTitle></SheetHeader>
              <OppForm initial={editing} onSaved={load} onClose={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 px-4 pb-2">
        {[["deals", "Deals"], ["contacts", "Contacts"]].map(([k, l]) => (
          <button key={k} data-testid={`pipeline-tab-${k}`} onClick={() => setView(k)}
            className={`h-11 text-xs font-bold uppercase tracking-widest tap-btn rounded-xl ${view === k ? "bg-primary text-primary-foreground shadow-sm" : "bg-card text-muted-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      {view === "contacts" ? <ContactsPanel /> : STAGES.map((stage) => {
        const items = opps.filter((o) => o.stage === stage);
        return (
          <section key={stage} className="soft-card mx-4 my-4 overflow-hidden">
            <div className="px-5 py-3 flex items-center justify-between bg-secondary/60">
              <p className="overline-label">{stage}</p>
              <p className="font-mono text-xs text-muted-foreground">{items.length} · {money(items.reduce((a, o) => a + (o.value || 0), 0))}</p>
            </div>
            {items.map((o) => (
              <div
                key={o.id}
                data-testid={`opportunity-row-${o.id}`}
                onClick={() => { setEditing(o); setOpen(true); }}
                className={`w-full text-left px-5 py-4 border-t border-border/60 cursor-pointer ${o.flagged ? "border-l-4 border-l-destructive" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold truncate">{o.client_name}</p>
                  <p className="font-mono text-sm text-primary shrink-0">
                    {money(o.value)}
                    {o.probability != null && <span className="text-muted-foreground text-[10px] ml-1.5">{o.probability}%</span>}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <p className="text-xs text-muted-foreground truncate">
                    {o.flagged ? (
                      <span className="text-destructive flex items-center gap-1"><AlertTriangle size={12} />NO NEXT ACTION SET</span>
                    ) : (
                      <>Next: {o.next_action} · <span className="font-mono">{o.next_action_date}</span></>
                    )}
                  </p>
                  {o.due_date && <p className="font-mono text-[10px] text-muted-foreground shrink-0">due {o.due_date}</p>}
                </div>
                {!["Won", "Lost"].includes(o.stage) && (
                  <div className="flex gap-2 mt-2">
                    <button
                      data-testid={`mark-won-${o.id}`}
                      onClick={(e) => { e.stopPropagation(); markOutcome(o, "Won"); }}
                      className="flex-1 h-9 border border-[#7A9B76] text-[#5f7f5b] text-[10px] font-bold uppercase tracking-widest tap-btn flex items-center justify-center gap-1"
                    >
                      <Trophy size={12} />Awarded
                    </button>
                    <button
                      data-testid={`mark-lost-${o.id}`}
                      onClick={(e) => { e.stopPropagation(); markOutcome(o, "Lost"); }}
                      className="flex-1 h-9 border border-border text-muted-foreground text-[10px] font-bold uppercase tracking-widest tap-btn flex items-center justify-center gap-1"
                    >
                      <XCircle size={12} />Not Awarded
                    </button>
                  </div>
                )}
              </div>
            ))}
            {items.length === 0 && <p className="px-5 py-3 text-xs text-muted-foreground border-t border-border/60">—</p>}
          </section>
        );
      })}
    </div>
  );
}
